import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  CapacityManagementRootResponse,
  TransactionsParams,
  TransactionsResponse,
} from 'src/apis/capacity-management/types';
import type { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params?: TransactionsParams;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params?: TransactionsParams;
};

export function useMyTransactions({
  capacityManagementRoot,
  params,
}: HookConfig) {
  const myTransactions = useAxios(
    (axios, baseConfig, { rootData, params }: RequestOptions) => {
      const link =
        rootData._links?.myTransactions ?? rootData._links?.myTransactionsV3;
      if (!link) throw new Forbidden();

      const url = urlTemplate.parse(link.href).expand({});

      return axios.request<TransactionsResponse>({
        ...baseConfig,
        url,
        params,
      });
    },
    {
      neededOnPageLoad: true,
      onError(error) {
        // as this is a non-lazy request we want to handle the error only,
        // if we have at least one successful response
        if (isConstraintViolation(error) && myTransactions.response) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, params };

    myTransactions.execute(options);
    // oxlint-disable-next-line exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return myTransactions;
}

export type MyTransactionsRequest = ReturnType<typeof useMyTransactions>;
